import React, { createContext, useContext, useMemo, useState } from 'react'
import { useSelector } from 'react-redux';
import { CustomizerContext } from '../context/CustomizerContext';
import { isYArray, isYMap } from '../hooks/documentHooks';

const SearchContext = createContext(null);

const useSearchTerm = () => useContext(SearchContext)

const SearchContextProvider = ({ scope, children }) => {
  const [searchTerm, setSearchTerm] = useState()
  const { document } = useContext(CustomizerContext);
  const currentData = useSelector(state => state.selectedLanguageData)
  const dataTranslationMap = document && currentData && document.getMap(currentData)

  const foundItems = useMemo(() => {
    if (!searchTerm) return
    if (searchTerm.term) return findInObject(scope, searchTerm.term, dataTranslationMap)
    if (searchTerm.path) return findPathInObject(searchTerm.path)
  }, [searchTerm, scope, dataTranslationMap])

  const data = useMemo(() => ({
    searchTerm,
    setSearchTerm,
    foundItems
  }), [searchTerm, setSearchTerm, foundItems])

  return (
    <SearchContext.Provider value={data}>
      {children}
    </SearchContext.Provider>
  )
}

const hasValueTerms = (value, terms) => {
  const exactMatches = (terms.exactTerms.length === 0) || !terms.exactTerms.find(term => !value.match(term))
  const normalizedValue = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  const fuzzyMatches = (terms.fuzzyTerms.length === 0) || !terms.fuzzyTerms.find(term => !normalizedValue.match(term))
  return exactMatches && fuzzyMatches
}

const setResult = (result, path, side) => {
  if (!result[path]) result[path] = {}
  result[path][side] = true
}

const _findInObject = (result, scope, terms, prefix, translation) => {
  if (isYMap(scope)) {
    for (let key of scope.keys()) {
      const path = prefix.length === 0 ? key : `${prefix}.${key}`
      const value = scope.get(key)
      _findInObject(result, value, terms, `${path}`, translation)
      if (hasValueTerms(key.toString(), terms)) setResult(result, path, 'key')
    }
  } else if (isYArray(scope)) {
    scope.toArray().forEach((value, key) => {
      _findInObject(result, value, terms, `${prefix}[${key}]`, translation)
    })
  } else {
    const newValue = translation && translation.get(prefix)
    const translatedValue = (newValue !== undefined && newValue !== null && newValue !== '') ? newValue : scope
    if (translatedValue !== undefined && translatedValue !== null && hasValueTerms(translatedValue.toString(), terms)) {
      setResult(result, `${prefix}`, 'value')
    }
  }
}

const findInObject = (scope, searchTerm, translation) => {
  if (!scope || !searchTerm) return {}

  const exactTerms = (searchTerm.match(/"(\\"|[^"])+"/g) || [])
    .map(item => new RegExp(item.substring(1, item.length - 1).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))

  const fuzzyTerms = searchTerm
    .replace(/"(\\"|[^"])+"/g, '')
    .trim()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .split(/[^a-z0-9.]+/i)
    .map(item => new RegExp(item.replace(/\./, '\\.'), 'i'))

  if (exactTerms.length === 0 && fuzzyTerms.length === 0) return {}

  const terms = { exactTerms, fuzzyTerms }
  const result = {}
  _findInObject(result, scope, terms, '', translation)
  return result
}

const findPathInObject = (searchPath) => {
  const result = {}
  setResult(result, searchPath, 'key')
  return result
}

export { useSearchTerm, SearchContextProvider }